import type { PayloadAction } from '@reduxjs/toolkit';
import type {
  ISchedulerGroup,
  ISchedulerMarker,
  ISchedulerTask,
  ISchedulerDependency,
  GroupId,
  SiteId,
  TaskId,
} from '@interfaces/IScheduler';
import { addDays, daysBetween } from '@components/Pages/SchedulerPage/lib/timeScale';
import { cascade } from '@components/Pages/SchedulerPage/lib/cascade';
import { childIndex } from '@components/Pages/SchedulerPage/lib/rollup';
import { sameJson } from '@components/Pages/SchedulerPage/lib/sameJson';
import type { CascadeFreeze } from '@components/Pages/SchedulerPage/lib/freeze';
import { splitTaskView, type ISchedulerPlanTask } from '@components/Pages/SchedulerPage/lib/taskViewCodec';
import { isPlanDay, isScheduled, taskDatesProblem } from '@components/Pages/SchedulerPage/lib/taskDates';
import type { ISchedulerState, ITaskPlanFields, ScheduleSubtreeScope } from './scheduler.state';

/**
 * The site's observed-work anchors, in the shape `cascade` takes: tasks the
 * field has already been seen working on, which the cascade flows around rather
 * than through (2026-09-20 amendment, decision 3). Empty for a schedule nobody
 * has started, which is the whole map's cost on an ordinary plan.
 */
function frozenIn(state: ISchedulerState, siteId: SiteId): ReadonlyMap<TaskId, CascadeFreeze> {
  return new Map(Object.entries(state.frozenBySite[siteId] ?? {}));
}

function wouldCycle(
  deps: ISchedulerDependency[],
  candidate: ISchedulerDependency,
  taskById?: Map<TaskId, ISchedulerPlanTask>,
): boolean {
  // DFS from candidate.successorId; if we reach candidate.predecessorId, it's a cycle.
  // `taskById` is accepted (but unused here) so callers can pass a prebuilt map for
  // consistency — the cycle check only needs the dep list, not the task map.
  void taskById;
  const adj = new Map<TaskId, TaskId[]>();
  for (const dependency of deps) {
    if (!adj.has(dependency.predecessorId)) adj.set(dependency.predecessorId, []);
    adj.get(dependency.predecessorId)!.push(dependency.successorId);
  }
  // include the candidate edge
  if (!adj.has(candidate.predecessorId)) adj.set(candidate.predecessorId, []);
  adj.get(candidate.predecessorId)!.push(candidate.successorId);

  const stack = [candidate.successorId];
  const visited = new Set<TaskId>();
  while (stack.length) {
    const id = stack.pop()!;
    if (id === candidate.predecessorId) return true;
    if (visited.has(id)) continue;
    visited.add(id);
    for (const next of adj.get(id) ?? []) stack.push(next);
  }
  return false;
}

/**
 * True when a dependency between `aId` and `bId` would span the same branch of
 * the task tree — i.e. one is an ancestor (parent/grandparent/…) of the other.
 * A summary's span already contains its descendants', so linking across that
 * relationship is a logical loop; MS Project blocks it ("you cannot create this
 * kind of relationship between a summary task and one of its subtasks").
 *
 * Accepts a prebuilt `taskById` map so the caller can build it once and reuse
 * it across both validators (avoids a redundant `new Map(tasks.map(...))` per call).
 */
function linkCrossesHierarchy(
  tasks: ISchedulerPlanTask[],
  aId: TaskId,
  bId: TaskId,
  taskById?: Map<TaskId, ISchedulerPlanTask>,
): boolean {
  const byId = taskById ?? new Map(tasks.map((task) => [task.id, task]));
  const isAncestorOf = (ancestorId: TaskId, nodeId: TaskId): boolean => {
    let current = byId.get(nodeId);
    const seen = new Set<TaskId>();
    while (current?.parentId != null && !seen.has(current.id)) {
      seen.add(current.id);
      if (current.parentId === ancestorId) return true;
      current = byId.get(current.parentId);
    }
    return false;
  };
  return isAncestorOf(aId, bId) || isAncestorOf(bId, aId);
}

export function moveTask(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; taskId: TaskId; deltaDays: number }>,
) {
  const site = state.bySite[action.payload.siteId];
  if (!site) return;
  const task = site.tasks.find((task) => task.id === action.payload.taskId);
  if (!task) return;
  // A summary task's span is derived from its descendant leaves — it can't be
  // dragged. Guard here so a stray dispatch (the bar itself isn't draggable)
  // is a no-op rather than corrupting the derived span.
  if (site.tasks.some((t) => t.parentId === task.id)) return;
  // An unscheduled row has no dates to shift. A task the field has been seen
  // working on has an observed start (any freeze pins it), so it cannot slide
  // — refused here, not only at the bar, so the arrow keys and any other
  // dispatcher obey the same rule as the drag.
  if (!isScheduled(task) || frozenIn(state, action.payload.siteId).has(task.id)) return;
  task.start = addDays(task.start, action.payload.deltaDays);
  task.end = addDays(task.end, action.payload.deltaDays);
  site.tasks = cascade(site.tasks, site.dependencies, [task.id], undefined, undefined, frozenIn(state, action.payload.siteId));
}

export function resizeTask(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; taskId: TaskId; edge: 'start' | 'end'; newDate: string }>,
) {
  const site = state.bySite[action.payload.siteId];
  if (!site) return;
  const task = site.tasks.find((task) => task.id === action.payload.taskId);
  if (!task) return;
  // Summary spans are derived — no manual resize (mirror moveTask's guard).
  if (site.tasks.some((t) => t.parentId === task.id)) return;
  if (!isScheduled(task) || !isPlanDay(action.payload.newDate)) return;
  // Freeze, as the bar's grips read it: any freeze pins the start, and only a
  // closed record (`all`) pins the end.
  const freeze = frozenIn(state, action.payload.siteId).get(task.id);
  if (freeze && (action.payload.edge === 'start' || freeze === 'all')) return;
  if (action.payload.edge === 'start') {
    task.start = action.payload.newDate;
    if (daysBetween(task.start, task.end) < 0) task.end = task.start;
  } else {
    task.end = action.payload.newDate;
    if (daysBetween(task.start, task.end) < 0) task.start = task.end;
  }
  site.tasks = cascade(site.tasks, site.dependencies, [task.id], undefined, undefined, frozenIn(state, action.payload.siteId));
}

export function addTask(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; task: ISchedulerTask; afterTaskId?: TaskId }>,
) {
  const site = state.bySite[action.payload.siteId];
  // A new row must be dated (the drawer's Save gate asks the same question).
  if (!site || taskDatesProblem(action.payload.task)) return;
  // Split the incoming task: the plan half joins `bySite`. A new task is never
  // COMMITTED — only a commit creates an overlay (invariant 2) — so a paste or
  // a create seeds no execution record, and the row reads as plan-only until
  // someone promises it.
  const { plan } = splitTaskView(action.payload.task);
  if (action.payload.afterTaskId) {
    const index = site.tasks.findIndex((task) => task.id === action.payload.afterTaskId);
    if (index !== -1) {
      site.tasks.splice(index + 1, 0, plan);
      return;
    }
  }
  site.tasks.push(plan);
}

export function addGroup(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; group: ISchedulerGroup; afterGroupId?: GroupId }>,
) {
  const site = state.bySite[action.payload.siteId];
  if (!site) return;
  if (action.payload.afterGroupId) {
    const index = site.groups.findIndex((group) => group.id === action.payload.afterGroupId);
    if (index !== -1) {
      site.groups.splice(index + 1, 0, action.payload.group);
      return;
    }
  }
  site.groups.push(action.payload.group);
}

export function deleteGroup(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; groupId: GroupId }>,
) {
  const site = state.bySite[action.payload.siteId];
  if (!site) return;
  const removedTaskIds = new Set(
    site.tasks.filter((task) => task.groupId === action.payload.groupId).map((task) => task.id),
  );
  site.groups = site.groups.filter((group) => group.id !== action.payload.groupId);
  site.tasks = site.tasks.filter((task) => task.groupId !== action.payload.groupId);
  site.dependencies = site.dependencies.filter(
    (dependency) =>
      !removedTaskIds.has(dependency.predecessorId) && !removedTaskIds.has(dependency.successorId),
  );
  if (state.ui.selectedTaskId && removedTaskIds.has(state.ui.selectedTaskId)) {
    state.ui.selectedTaskId = null;
  }
  if (state.ui.panelOpen?.taskId && removedTaskIds.has(state.ui.panelOpen.taskId)) {
    state.ui.panelOpen = null;
  }
}

/** `rootId` and every task below it — the closure over `parentId`. */
function subtreeIds(tasks: readonly ISchedulerPlanTask[], rootId: TaskId): Set<TaskId> {
  const children = childIndex(tasks);
  const ids = new Set<TaskId>();
  const queue: TaskId[] = [rootId];
  while (queue.length) {
    const id = queue.shift()!;
    if (ids.has(id)) continue;
    ids.add(id);
    for (const child of children.get(id) ?? []) queue.push(child);
  }
  return ids;
}

export function updateTask(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; task: ISchedulerTask }>,
) {
  const site = state.bySite[action.payload.siteId];
  if (!site) return;
  const taskIndex = site.tasks.findIndex((task) => task.id === action.payload.task.id);
  if (taskIndex === -1) return;
  // Whole-task Save is a PLAN update: strip the payload's execution fields
  // (the drawer draft snapshot is stale for them) and leave `activityBySite`
  // untouched, so an in-flight slider/status edit is never reverted.
  const { plan } = splitTaskView(action.payload.task);
  // A drawer Save with nothing edited is not an edit: no history entry, no
  // dirty flag, no cascade (a fresh object would read as a change to Immer).
  if (sameJson(site.tasks[taskIndex], plan)) return;
  // Invalid dates never enter the plan — they would throw in the cascade below
  // and in every renderer after it (the drawer gates Save on the same rule).
  if (taskDatesProblem(plan)) return;
  const previousGroupId = site.tasks[taskIndex]!.groupId;
  site.tasks[taskIndex] = plan;
  // A sub-task lives in its parent's group, so moving a summary to another
  // group takes its whole subtree along — left behind, the children would
  // render under the parent in the new group and be stranded by a delete of
  // the old one.
  if (plan.groupId !== previousGroupId) {
    const subtree = subtreeIds(site.tasks, plan.id);
    for (const task of site.tasks) if (subtree.has(task.id)) task.groupId = plan.groupId;
  }
  // Reschedule successors. On a constraint-consistent schedule this is a
  // no-op for a plan-only edit (no dates moved → nothing to reflow); it only
  // shifts dates when the edit — or the upstream data — actually violates a link.
  site.tasks = cascade(site.tasks, site.dependencies, [plan.id], undefined, undefined, frozenIn(state, action.payload.siteId));
}

/**
 * The drawer's auto-saved plan fields (2026-09-23 amendment): profession and
 * crew size, already on their way to the server through their own PUT, so —
 * like area assignment — neither undoable nor dirty-marking. `null` clears.
 */
export function taskPlanFieldsSet(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; taskId: TaskId; fields: ITaskPlanFields }>,
) {
  const task = state.bySite[action.payload.siteId]?.tasks.find((t) => t.id === action.payload.taskId);
  if (!task) return;
  const { profession, workerCount } = action.payload.fields;
  if (profession === null) delete task.profession;
  else if (profession !== undefined) task.profession = profession;
  if (workerCount === null) delete task.workerCount;
  else if (workerCount !== undefined) task.workerCount = workerCount;
}

export function deleteTask(state: ISchedulerState, action: PayloadAction<{ siteId: SiteId; taskId: TaskId }>) {
  const site = state.bySite[action.payload.siteId];
  if (!site) return;
  // Deleting a summary task cascades to its whole subtree: remove every task
  // in it + any dependency touching one + clear a selection/panel pointing at
  // one (mirror deleteGroup).
  const removedTaskIds = subtreeIds(site.tasks, action.payload.taskId);
  site.tasks = site.tasks.filter((task) => !removedTaskIds.has(task.id));
  site.dependencies = site.dependencies.filter(
    (dependency) => !removedTaskIds.has(dependency.predecessorId) && !removedTaskIds.has(dependency.successorId),
  );
  if (state.ui.selectedTaskId && removedTaskIds.has(state.ui.selectedTaskId)) {
    state.ui.selectedTaskId = null;
  }
  if (state.ui.panelOpen?.taskId && removedTaskIds.has(state.ui.panelOpen.taskId)) {
    state.ui.panelOpen = null;
  }
}

/**
 * Bulk-apply auto-colorize results: set `color` on every task present in the
 * map (non-milestone tasks; the engine omits milestones, which take the
 * separate ui.milestoneColor). One action → one undo entry, and `color` is a
 * persisted EDIT_FIELD so it marks the site dirty for Save. The payload is a
 * plain Record (serializable) — callers convert the engine's Map at dispatch.
 */
export function applyTaskColors(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; colorByTaskId: Record<TaskId, string> }>,
) {
  const site = state.bySite[action.payload.siteId];
  if (!site) return;
  for (const task of site.tasks) {
    const color = action.payload.colorByTaskId[task.id];
    if (color) task.color = color;
  }
}

/**
 * Atomically replace one task with a set of per-location leaf subtasks: the
 * task becomes a summary (its bar span is derived from the new children) and
 * the optional FS links chain them in order. Computed by
 * `buildLocationSplit` in the Split modal — this reducer just applies the
 * precomputed arrays so the whole split is a SINGLE undo entry (no
 * per-dependency cascade churn).
 */
export function splitTaskByLocations(
  state: ISchedulerState,
  action: PayloadAction<{
    siteId: SiteId;
    parentTaskId: TaskId;
    subtasks: ISchedulerTask[];
    dependencies: ISchedulerDependency[];
  }>,
) {
  const { siteId } = action.payload;
  const site = state.bySite[siteId];
  if (!site) return;
  const index = site.tasks.findIndex((task) => task.id === action.payload.parentTaskId);
  if (index === -1) return;
  // The parent becomes a summary and the subtasks own the areas — drop the
  // parent's own assignments (plan) so they aren't double-counted.
  const parentTask = site.tasks[index];
  if (parentTask) parentTask.locations = [];
  // Split each subtask: the plan half heads the parent's subtree. None of them
  // is committed — a split produces new rows, and only a commit makes a promise.
  const planSubtasks: ISchedulerPlanTask[] = [];
  for (const sub of action.payload.subtasks) {
    planSubtasks.push(splitTaskView(sub).plan);
  }
  // Splice the children in right after the parent so they head its subtree.
  site.tasks.splice(index + 1, 0, ...planSubtasks);
  site.dependencies.push(...action.payload.dependencies);
  // Dates were built consistent with the new links; one cascade keeps the
  // graph's invariants without re-running per dependency.
  site.tasks = cascade(
    site.tasks,
    site.dependencies,
    planSubtasks.map((task) => task.id),
    undefined,
    undefined,
    frozenIn(state, action.payload.siteId),
  );
}

export function addDependency(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; dependency: ISchedulerDependency }>,
) {
  const site = state.bySite[action.payload.siteId];
  if (!site) return;
  if (action.payload.dependency.predecessorId === action.payload.dependency.successorId) {
    state.ui.lastError = 'cycleDetected';
    return;
  }
  // Build once, reuse across both validators.
  const taskById = new Map(site.tasks.map((t) => [t.id, t]));
  if (wouldCycle(site.dependencies, action.payload.dependency, taskById)) {
    state.ui.lastError = 'cycleDetected';
    return;
  }
  if (linkCrossesHierarchy(site.tasks, action.payload.dependency.predecessorId, action.payload.dependency.successorId, taskById)) {
    state.ui.lastError = 'cycleDetected';
    return;
  }
  site.dependencies.push(action.payload.dependency);
  site.tasks = cascade(
    site.tasks,
    site.dependencies,
    [action.payload.dependency.predecessorId],
    undefined,
    undefined,
    frozenIn(state, action.payload.siteId),
  );
  state.ui.lastError = null;
}

export function deleteDependency(state: ISchedulerState, action: PayloadAction<{ siteId: SiteId; dependencyId: string }>) {
  const site = state.bySite[action.payload.siteId];
  if (!site) return;
  site.dependencies = site.dependencies.filter((dependency) => dependency.id !== action.payload.dependencyId);
}

export function updateDependency(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; dependency: ISchedulerDependency }>,
) {
  const site = state.bySite[action.payload.siteId];
  if (!site) return;
  const index = site.dependencies.findIndex((dependency) => dependency.id === action.payload.dependency.id);
  if (index === -1) return;
  const existing = site.dependencies[index]!;
  // Defensive: if pred/succ changed, verify the proposed graph is still acyclic.
  if (
    existing.predecessorId !== action.payload.dependency.predecessorId ||
    existing.successorId !== action.payload.dependency.successorId
  ) {
    // Build once, reuse across both validators.
    const taskById = new Map(site.tasks.map((t) => [t.id, t]));
    // Build the proposed dep list: swap the existing entry for the incoming one.
    const proposed = site.dependencies.filter((dependency) => dependency.id !== action.payload.dependency.id);
    if (wouldCycle(proposed, action.payload.dependency, taskById)) {
      state.ui.lastError = 'cycleDetected';
      return;
    }
    if (linkCrossesHierarchy(site.tasks, action.payload.dependency.predecessorId, action.payload.dependency.successorId, taskById)) {
      state.ui.lastError = 'cycleDetected';
      return;
    }
  }
  // Snapshot the pre-edit list (plain clones — the drafts mutate next line):
  // cascade uses it to spot successors pinned to their old constraint and
  // pull them back when the edit allows an earlier date (e.g. lag reduced).
  const previousDeps = site.dependencies.map((dependency) => ({ ...dependency }));
  site.dependencies[index] = action.payload.dependency;
  // Re-run cascade from the predecessor so changes propagate.
  site.tasks = cascade(
    site.tasks,
    site.dependencies,
    [action.payload.dependency.predecessorId],
    undefined,
    previousDeps,
    frozenIn(state, action.payload.siteId),
  );
  state.ui.lastError = null;
}

export function toggleGroupCollapsed(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; groupId: GroupId }>,
) {
  const site = state.bySite[action.payload.siteId];
  if (!site) return;
  const group = site.groups.find((group) => group.id === action.payload.groupId);
  if (group) group.collapsed = !group.collapsed;
}

/**
 * Flip a summary task's collapse flag (mirror toggleGroupCollapsed). Pure
 * view state — undoable but NOT syncable, so it never marks the site dirty
 * and is never sent to the backend.
 */
export function toggleTaskCollapsed(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; taskId: TaskId }>,
) {
  const site = state.bySite[action.payload.siteId];
  if (!site) return;
  const task = site.tasks.find((task) => task.id === action.payload.taskId);
  if (task) task.collapsed = !task.collapsed;
}

/**
 * Expand (collapsed=false) or collapse (collapsed=true) ONE subtree of
 * summary rows — scoped to the group or task whose arrow was right-clicked,
 * not the whole schedule. View state only (undoable, never synced).
 *   - group scope: every summary TASK in the group; the group itself only
 *     opens on expand (on collapse it stays as-is so its top level shows).
 *   - task scope:  on expand, the clicked task + all descendant summaries
 *     open; on collapse, the descendants collapse while the clicked task
 *     stays open (so you still see its immediate children).
 */
export function setSubtreeCollapsed(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; scope: ScheduleSubtreeScope; collapsed: boolean }>,
) {
  const { siteId, scope, collapsed } = action.payload;
  const site = state.bySite[siteId];
  if (!site) return;
  const childrenByParent = new Map<string, ISchedulerPlanTask[]>();
  for (const task of site.tasks) {
    if (task.parentId == null) continue;
    const siblings = childrenByParent.get(task.parentId);
    if (siblings) siblings.push(task);
    else childrenByParent.set(task.parentId, [task]);
  }
  const isSummary = (id: string) => childrenByParent.has(id);

  if (scope.kind === 'group') {
    const group = site.groups.find((g) => g.id === scope.groupId);
    if (!group) return;
    if (!collapsed) group.collapsed = false;
    for (const task of site.tasks) {
      if (task.groupId === scope.groupId && isSummary(task.id)) task.collapsed = collapsed;
    }
    return;
  }

  const root = site.tasks.find((task) => task.id === scope.taskId);
  if (!root) return;
  if (!collapsed) root.collapsed = false; // expand opens the clicked task too
  const stack = [...(childrenByParent.get(root.id) ?? [])];
  while (stack.length > 0) {
    const task = stack.pop()!;
    const kids = childrenByParent.get(task.id);
    if (kids) {
      task.collapsed = collapsed;
      stack.push(...kids);
    }
  }
}

/**
 * Open the path to one task: its group and every summary above it. What a
 * link to a task does before it can land on the row — a collapsed parent
 * hides it. View state only, and not undoable: the user didn't toggle
 * anything, a link did.
 */
export function revealTask(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; taskId: TaskId }>,
) {
  const site = state.bySite[action.payload.siteId];
  if (!site) return;
  const taskById = new Map(site.tasks.map((task) => [task.id, task] as const));
  const task = taskById.get(action.payload.taskId);
  if (!task) return;
  for (const group of site.groups) if (group.id === task.groupId) group.collapsed = false;
  for (let parent = taskById.get(task.parentId ?? ''); parent; parent = taskById.get(parent.parentId ?? '')) {
    parent.collapsed = false;
  }
}

export function addMarker(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; marker: ISchedulerMarker }>,
) {
  const site = state.bySite[action.payload.siteId];
  if (!site) return;
  site.markers ??= [];
  site.markers.push(action.payload.marker);
}

export function updateMarker(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; marker: ISchedulerMarker }>,
) {
  const site = state.bySite[action.payload.siteId];
  if (!site) return;
  site.markers ??= [];
  const index = site.markers.findIndex((marker) => marker.id === action.payload.marker.id);
  if (index === -1) return;
  site.markers[index] = action.payload.marker;
}

export function deleteMarker(
  state: ISchedulerState,
  action: PayloadAction<{ siteId: SiteId; markerId: string }>,
) {
  const site = state.bySite[action.payload.siteId];
  if (!site) return;
  site.markers ??= [];
  site.markers = site.markers.filter((marker) => marker.id !== action.payload.markerId);
}
